import { useState, useEffect, useCallback } from "react";
import { generateFakeMeterReadings } from "../fakeData";
import { apiFetch } from "../apiFetch";

const POLL_INTERVAL_MS = 60 * 1000;

// The pilot meter reports about once a minute, so this is much tighter than the
// weather station's two hours. It is still meant to catch "this channel stopped
// reporting", not "this arrived a bit late".
const STALE_AFTER_MS = 15 * 60 * 1000;

// Display order and units, grouped the way an operator reads a meter: what the
// line is doing right now first, then how the load is behaving, then the totals.
//
// Units are the confirmed ones — see DataIngestionESM/lib/adw300.mjs for how each
// scale factor was checked. This renders what the API sends without rescaling
// anything, so there is exactly one place a divisor can be wrong.
const GROUPS = [
  {
    titleKey: "pmGroupLine",
    fields: [
      { key: "voltage_a_v", labelKey: "pmVoltageA", unit: "V", decimals: 1 },
      { key: "current_a_a", labelKey: "pmCurrentA", unit: "A", decimals: 2 },
      { key: "voltage_b_v", labelKey: "pmVoltageB", unit: "V", decimals: 1 },
      { key: "current_b_a", labelKey: "pmCurrentB", unit: "A", decimals: 2 },
      { key: "voltage_c_v", labelKey: "pmVoltageC", unit: "V", decimals: 1 },
      { key: "current_c_a", labelKey: "pmCurrentC", unit: "A", decimals: 2 },
      { key: "voltage_ab_v", labelKey: "pmVoltageAB", unit: "V", decimals: 1 },
      { key: "voltage_bc_v", labelKey: "pmVoltageBC", unit: "V", decimals: 1 },
      { key: "voltage_ca_v", labelKey: "pmVoltageCA", unit: "V", decimals: 1 },
    ],
  },
  {
    titleKey: "pmGroupPower",
    fields: [
      { key: "active_power_kw", labelKey: "pmActivePower", unit: "kW", decimals: 3, signed: true },
      { key: "reactive_power_kvar", labelKey: "pmReactivePower", unit: "kvar", decimals: 3, signed: true },
      { key: "apparent_power_kva", labelKey: "pmApparentPower", unit: "kVA", decimals: 3 },
      { key: "power_factor", labelKey: "pmPowerFactor", unit: "", decimals: 3 },
    ],
  },
  {
    titleKey: "pmGroupEnergy",
    fields: [
      { key: "energy_total_kwh", labelKey: "pmEnergyTotal", unit: "kWh", decimals: 2 },
      { key: "energy_forward_kwh", labelKey: "pmEnergyForward", unit: "kWh", decimals: 2 },
      { key: "energy_reverse_kwh", labelKey: "pmEnergyReverse", unit: "kWh", decimals: 2 },
      { key: "reactive_energy_forward_kvarh", labelKey: "pmReactiveEnergyForward", unit: "kvarh", decimals: 2 },
      { key: "reactive_energy_reverse_kvarh", labelKey: "pmReactiveEnergyReverse", unit: "kvarh", decimals: 2 },
    ],
  },
];

// Transformer ratios. Configuration rather than measurement, so they sit in the
// footer with the device metadata instead of among the readings.
const CONFIG_FIELDS = [
  { key: "pt_ratio", labelKey: "pmPtRatio" },
  { key: "ct_ratio", labelKey: "pmCtRatio" },
];

function formatAge(iso, t) {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return null;
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return t("wsJustNow");
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

function isStale(iso) {
  if (!iso) return false;
  return Date.now() - new Date(iso).getTime() > STALE_AFTER_MS;
}

// `simulated` renders generated data and makes no network request. This is not
// only cosmetic: every tier below superuser is simulated, and fetching here would
// put real site measurements in front of them.
export default function PowerMeterPanel({ deviceId, apiBaseUrl, label, t = (k) => k, simulated = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      let json;
      if (simulated) {
        json = generateFakeMeterReadings(deviceId);
      } else {
        const url = `${apiBaseUrl}/api/meter?device_id=${encodeURIComponent(deviceId)}`;
        const res = await apiFetch(url);
        if (!res.ok) throw new Error(`API error ${res.status}`);
        json = await res.json();
      }
      setData(json);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [deviceId, apiBaseUrl, simulated]);

  useEffect(() => {
    setLoading(true);
    fetchData();
    const interval = setInterval(fetchData, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchData]);

  const readings = data?.readings ?? {};

  // A field is rendered only when the API actually sent it. Nothing here supplies
  // a fallback, because on an electrical meter a fabricated 0 V or 0 kW reads as a
  // dead line — an alarm-grade false signal rather than a cosmetic blank (#33/#57).
  const has = (key) => readings[key]?.value !== undefined && readings[key]?.value !== null;

  const groups = GROUPS
    .map((g) => ({ ...g, present: g.fields.filter((f) => has(f.key)) }))
    .filter((g) => g.present.length > 0);

  const config = CONFIG_FIELDS.filter((f) => has(f.key));
  const withheld = data?.unverified_fields ?? [];
  const phases = data?.phases_present ?? [];
  const anyReading = groups.length > 0;

  return (
    <div style={{ ...styles.card, maxWidth: 1600 }}>
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>{label ?? deviceId}</h2>
          <p style={styles.subtitle}>{deviceId}</p>
        </div>
        {phases.length > 0 && (
          <span style={styles.phaseBadge}>
            {phases.length === 1 ? t("pmSinglePhase") : t("pmPhaseCount").replace("{n}", phases.length)}
            {" · "}
            {phases.map((p) => p.toUpperCase()).join(", ")}
          </span>
        )}
      </div>

      <div style={styles.metaRow}>
        <span style={styles.lastUpdated}>
          {lastUpdated ? `${t("updated")} ${lastUpdated.toLocaleTimeString()}` : t("loading")}
        </span>
      </div>

      {loading ? (
        <div style={styles.placeholder}>{t("driftChartLoading")}</div>
      ) : error ? (
        <div style={styles.errorBox}>{t("driftChartError")}: {error}</div>
      ) : !anyReading ? (
        <div style={styles.placeholder}>{t("driftChartNoData")}</div>
      ) : (
        groups.map((group) => (
          <div key={group.titleKey} style={styles.group}>
            <h3 style={styles.groupTitle}>{t(group.titleKey)}</h3>
            <table style={styles.table}>
              <tbody>
                {group.present.map((f) => {
                  const r = readings[f.key];
                  const stale = isStale(r.measured_at);
                  const num = Number(r.value);
                  const shown = Number.isFinite(num) ? num.toFixed(f.decimals) : String(r.value);
                  // The meter reports active and reactive power signed, and the
                  // sign carries information about direction of flow, so it is
                  // shown explicitly rather than being dropped or abs()'d. Which
                  // direction negative means is not yet confirmed — see
                  // infrastructure/POWER_DATA.md.
                  const signed = f.signed && Number.isFinite(num) && num > 0 ? `+${shown}` : shown;
                  return (
                    <tr key={f.key} style={styles.row}>
                      <td style={styles.labelCell}>{t(f.labelKey)}</td>
                      <td style={{ ...styles.valueCell, ...(stale ? styles.staleValue : null) }}>
                        {signed}{f.unit && <span style={styles.unit}>{f.unit}</span>}
                      </td>
                      <td style={styles.ageCell}>
                        {stale ? `${formatAge(r.measured_at, t)} ${t("wsAgo")}` : formatAge(r.measured_at, t)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))
      )}

      {!loading && !error && anyReading && (
        <>
          {/* Direction of flow is unresolved, and on a meter that is worth saying
              out loud rather than leaving a reader to infer it from a minus sign. */}
          {readings.active_power_kw?.value < 0 && (
            <p style={styles.note}>{t("pmNegativePowerNote")}</p>
          )}
          {config.length > 0 && (
            <p style={styles.config}>
              {config.map((f) => `${t(f.labelKey)} ${readings[f.key].value}`).join(" · ")}
              {readings.ct_ratio?.value === 1 && ` — ${t("pmCtRatioOneNote")}`}
            </p>
          )}
        </>
      )}

      {/* The meter also reports channels whose meaning or scaling is not
          confirmed. They are stored and returned by the API, but showing them
          here as readings would present guesses as measurements. */}
      {!loading && !error && withheld.length > 0 && (
        <p style={styles.withheld}>
          {t("pmUnverifiedNote")} <code style={styles.code}>{withheld.join(", ")}</code>
        </p>
      )}
    </div>
  );
}

const styles = {
  card: { background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 8, padding: "20px 24px", fontFamily: "'Inter', system-ui, sans-serif" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 },
  title: { margin: 0, fontSize: 18, fontWeight: 600, color: "#111827" },
  subtitle: { margin: "2px 0 0", fontSize: 12, color: "#9ca3af" },
  phaseBadge: { background: "#eff6ff", color: "#1d4ed8", padding: "3px 10px", borderRadius: 12, fontSize: 13, fontWeight: 500, whiteSpace: "nowrap" },
  metaRow: { display: "flex", alignItems: "center", marginBottom: 12 },
  lastUpdated: { marginLeft: "auto", fontSize: 12, color: "#9ca3af" },
  placeholder: { height: 120, display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af", fontSize: 14 },
  errorBox: { height: 80, display: "flex", alignItems: "center", justifyContent: "center", color: "#ef4444", fontSize: 14, background: "#fef2f2", borderRadius: 6 },
  group: { marginBottom: 10 },
  groupTitle: { margin: "6px 0 2px", fontSize: 12, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.4 },
  table: { width: "100%", borderCollapse: "collapse" },
  row: { borderBottom: "1px solid #f3f4f6" },
  labelCell: { padding: "10px 8px 10px 0", fontSize: 14, color: "#374151", fontWeight: 500 },
  valueCell: { padding: "10px 8px", fontSize: 18, fontWeight: 600, color: "#111827", textAlign: "right", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" },
  staleValue: { color: "#9ca3af" },
  unit: { fontSize: 12, fontWeight: 500, color: "#6b7280", marginLeft: 4 },
  ageCell: { padding: "10px 0 10px 8px", fontSize: 12, color: "#9ca3af", textAlign: "right", width: 90, whiteSpace: "nowrap" },
  note: { marginTop: 12, marginBottom: 0, fontSize: 12, color: "#b45309", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, padding: "8px 10px", lineHeight: 1.5 },
  config: { marginTop: 12, marginBottom: 0, fontSize: 12, color: "#6b7280", lineHeight: 1.5 },
  withheld: { marginTop: 10, marginBottom: 0, fontSize: 12, color: "#9ca3af", lineHeight: 1.5 },
  code: { background: "#f3f4f6", padding: "1px 5px", borderRadius: 4, fontSize: 11, color: "#6b7280" },
};
